import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { createInstaller } from './api';
import { useIsMobile } from '../../hooks/useIsMobile';
const INITIAL_FORM = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    skills: [],
};
export const InstallerCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [form, setForm] = useState(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [newSkill, setNewSkill] = useState('');
    const set = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    };
    const validate = () => {
        const errors = {};
        if (!form.firstName.trim())
            errors.firstName = 'First name is required.';
        if (!form.lastName.trim())
            errors.lastName = 'Last name is required.';
        if (!form.email.trim()) {
            errors.email = 'Email is required.';
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            errors.email = 'Please enter a valid email address.';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const addSkill = () => {
        const trimmed = newSkill.trim();
        if (!trimmed || form.skills.includes(trimmed))
            return;
        setForm((p) => ({ ...p, skills: [...p.skills, trimmed] }));
        setNewSkill('');
    };
    const removeSkill = (skill) => {
        setForm((p) => ({ ...p, skills: p.skills.filter((s) => s !== skill) }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate())
            return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const installer = await createInstaller({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || undefined,
                skills: form.skills,
            });
            navigate(`/installers/${installer.id}`);
        }
        catch (e) {
            setSubmitError(e.message ?? 'Failed to create installer');
        }
        finally {
            setSubmitting(false);
        }
    };
    const inputStyle = {
        ...styles.input,
        fontSize: isMobile ? '16px' : '15px',
    };
    const cardStyle = {
        ...styles.card,
        padding: isMobile ? '20px 16px' : '28px 32px',
    };
    const twoColStyle = {
        ...styles.twoCol,
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: { maxWidth: '600px' }, children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/installers'), children: "\u2190 Team" }), _jsxs("div", { style: styles.pageHeader, children: [_jsx("h2", { style: styles.heading, children: "New Team Member" }), _jsx("p", { style: styles.subheading, children: "Add a new field team member" })] }), _jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Personal Info" }), _jsxs("div", { style: twoColStyle, children: [_jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["First Name ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.firstName ? styles.inputError : {}),
                                                    }, type: "text", value: form.firstName, onChange: set('firstName'), placeholder: "Jane" }), fieldErrors.firstName && (_jsx("p", { style: styles.fieldError, children: fieldErrors.firstName }))] }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["Last Name ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.lastName ? styles.inputError : {}),
                                                    }, type: "text", value: form.lastName, onChange: set('lastName'), placeholder: "Smith" }), fieldErrors.lastName && (_jsx("p", { style: styles.fieldError, children: fieldErrors.lastName }))] })] }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["Email ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                ...inputStyle,
                                                ...(fieldErrors.email ? styles.inputError : {}),
                                            }, type: "email", value: form.email, onChange: set('email'), placeholder: "jane.smith@example.com" }), fieldErrors.email && _jsx("p", { style: styles.fieldError, children: fieldErrors.email })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Phone" }), _jsx("input", { style: inputStyle, type: "tel", value: form.phone, onChange: set('phone'), placeholder: "(555) 000-0000" })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Skills" }), form.skills.length > 0 && (_jsx("div", { style: styles.skillsRow, children: form.skills.map((skill) => (_jsxs("span", { style: styles.skillChipEdit, children: [skill, _jsx("button", { type: "button", style: styles.skillRemoveBtn, onClick: () => removeSkill(skill), "aria-label": `Remove ${skill}`, children: "\u00D7" })] }, skill))) })), _jsxs("div", { style: { display: 'flex', gap: '8px', marginTop: form.skills.length > 0 ? '12px' : '0' }, children: [_jsx("input", { style: { ...inputStyle, maxWidth: '260px' }, type: "text", placeholder: "e.g. Window Installation", value: newSkill, onChange: (e) => setNewSkill(e.target.value), onKeyDown: (e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addSkill();
                                                }
                                            } }), _jsx("button", { type: "button", style: styles.addItemButton, onClick: addSkill, children: "Add" })] })] }), submitError && _jsx("div", { style: styles.errorBanner, children: submitError }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: styles.ghostButton, onClick: () => navigate('/installers'), disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", style: styles.primaryButton, disabled: submitting, children: submitting ? 'Creating…' : 'Create Team Member' })] })] })] }) }));
};
const styles = {
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    pageHeader: {
        marginBottom: '24px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    twoCol: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 20px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginBottom: '16px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    required: {
        color: '#ff3b30',
    },
    input: {
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    inputError: {
        borderColor: 'rgba(255,59,48,0.6)',
        backgroundColor: 'rgba(255,59,48,0.04)',
    },
    fieldError: {
        margin: '2px 0 0 0',
        fontSize: '12px',
        color: '#c0392b',
        fontWeight: '500',
    },
    skillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    skillChipEdit: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px 4px 12px',
        backgroundColor: 'rgba(0,122,255,0.08)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    skillRemoveBtn: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '700',
        lineHeight: '1',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
    },
    addItemButton: {
        padding: '9px 18px',
        backgroundColor: 'transparent',
        color: '#0071e3',
        border: '1px solid #0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '40px',
    },
    primaryButton: {
        padding: '12px 28px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    ghostButton: {
        padding: '12px 28px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        minHeight: '44px',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
};
//# sourceMappingURL=InstallerCreate.js.map